import { FC, useState } from 'react';

import Button from 'primitives/Button';
import VerticalTimeline from 'primitives/Timeline/VerticalTimeline';

import unitTransferEventTypeToReadable from 'utils/enums/unit-transfer-event-type-to-readable';
import unitTransferEventTypeToTimelineStatus from 'utils/enums/unit-transfer-event-type-to-timeline-status';

type UnitTransferEvent = {
  id: string;
  type: any;
  note?: string | null;
  createdAt: string;
  actor?: { name: string } | null;
};

const PAGE_SIZE = 5;

const Timeline: FC<{ events: UnitTransferEvent[] }> = ({ events }) => {
  // events arrives oldest-first (the request's actual chronology) — reversed here so the most
  // recent update reads first, like any other activity feed. A long transfer can rack up a
  // dozen-plus rows, so only PAGE_SIZE show initially; Load More reveals older ones further down.
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  if (events.length === 0) {
    return <p className="text-sm text-gray-500">No activity yet.</p>;
  }

  const mostRecentFirst = [...events].reverse();
  const visibleEvents = mostRecentFirst.slice(0, visibleCount);
  const hiddenCount = mostRecentFirst.length - visibleEvents.length;

  return (
    <div>
      {visibleEvents.map((event, index) => (
        <VerticalTimeline
          key={event.id}
          label={unitTransferEventTypeToReadable(event.type)}
          date={event.createdAt}
          showTime
          // Rejections and send-backs used to render with a green tick because every row was
          // hard-coded to "success".
          status={unitTransferEventTypeToTimelineStatus(event.type)}
          // Every row would otherwise carry a "Success" pill, which says nothing the coloured
          // indicator does not already say.
          hideStatusLabel
          description={
            event.actor?.name || event.note ? (
              <>
                {event.actor?.name ? (
                  <p className="text-xs text-gray-500">by {event.actor.name}</p>
                ) : null}
                {/* The note carries the reason a request was rejected or documents were sent
                    back — the part the investor actually needs to act on. */}
                {event.note ? <p className="mt-0.5">{event.note}</p> : null}
              </>
            ) : null
          }
          isLast={index === visibleEvents.length - 1 && hiddenCount === 0}
        />
      ))}
      {hiddenCount > 0 ? (
        <Button
          variant="text"
          className="ml-7"
          onClick={() => setVisibleCount(count => count + PAGE_SIZE)}
        >
          Load {Math.min(hiddenCount, PAGE_SIZE)} more
        </Button>
      ) : null}
    </div>
  );
};

export default Timeline;
